import { forwardRef, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Carousel } from 'react-responsive-carousel';

import { trans } from '@spotahome/soyuz/client';

import { RATIOS } from '../ImageCarousel/utils/constants';

import GalleryArrow from '../GalleryArrow';

import SliderTrackVideo from './SliderTrackVideo';

import styles from './VideoCarousel.module.scss';

const VideoCarousel = ({
  items,
  className = '',
  keyboardHandleEnabled = false,
  ratio = RATIOS.PANORAMIC,
  forwardedRef = () => {},
  keepHeightWhileLoading = false,
  lazyLoad = false,
  withBackgroundChevrons = false,
  isShowStatus = false,
  autoFocus = false,
  chevronStyle = 'small'
}) => {
  const [isLoaded, setIsLoaded] = useState(!lazyLoad);
  const [currentIndex, setCurrentIndex] = useState(0);
  const lazyAction = useRef(null);

  useEffect(() => {
    if (isLoaded && lazyAction.current) {
      lazyAction.current();
      lazyAction.current = null;
    }
  }, [currentIndex, isLoaded, items.length]);

  const handleSwipeStart = () => {
    if (!isLoaded) {
      setIsLoaded(true);
    }
  };

  const handlePreviousItem = goToPrevItem => e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!isLoaded) {
      lazyAction.current = goToPrevItem;
      setIsLoaded(true);
    } else {
      goToPrevItem();
    }
  };

  const handleNextItem = goToNextItem => e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!isLoaded) {
      lazyAction.current = goToNextItem;
      setIsLoaded(true);
    } else {
      goToNextItem();
    }
  };

  const handleItemChange = newIndex => {
    setCurrentIndex(newIndex);
  };

  const galleryClassName = classNames(className, styles['video-carousel']);
  const wrapClassName = classNames(styles['video-carousel__wrap'], {
    [styles[`video-carousel__wrap--keep-height-${ratio.replace(':', '-')}`]]:
      keepHeightWhileLoading
  });
  const constantHeightWrapClassName = keepHeightWhileLoading
    ? styles['video-carousel__keep-height-wrapper']
    : '';

  return (
    <div className={galleryClassName}>
      <div className={wrapClassName}>
        <div className={constantHeightWrapClassName}>
          <Carousel
            autoFocus={autoFocus}
            preventMovementUntilSwipeScrollTolerance
            swipeScrollTolerance={100}
            useKeyboardArrows={keyboardHandleEnabled}
            showArrows={false}
            showIndicators={false}
            showThumbs={false}
            showStatus={isShowStatus}
            onSwipeStart={handleSwipeStart}
            renderArrowPrev={onClick =>
              items.length > 1 ? (
                <GalleryArrow
                  style={chevronStyle}
                  navRole="prev"
                  onClick={handlePreviousItem(onClick)}
                  dataTest="gallery-arrow-prev"
                  withBackground={withBackgroundChevrons}
                />
              ) : null
            }
            renderArrowNext={onClick =>
              items.length > 1 ? (
                <GalleryArrow
                  style={chevronStyle}
                  navRole="next"
                  onClick={handleNextItem(onClick)}
                  dataTest="gallery-arrow-next"
                  withBackground={withBackgroundChevrons}
                />
              ) : null
            }
            onChange={handleItemChange}
            onClickItem={() => {}}
            renderItem={(item, { isSelected }) => {
              return <SliderTrackVideo {...item.props} playing={isSelected} />;
            }}
          >
            {items.map(item => (
              <SliderTrackVideo key={item.video.videoId} item={item} />
            ))}
          </Carousel>
        </div>
      </div>

      <div className={styles['video-carousel__counter']} ref={forwardedRef}>
        {currentIndex + 1}/{items.length} {trans('slider.counter.videos')}
      </div>
    </div>
  );
};

VideoCarousel.propTypes = {
  className: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      renderItem: PropTypes.func,
      src: PropTypes.string.isRequired,
      caption: PropTypes.string.isRequired
    })
  ).isRequired,
  keyboardHandleEnabled: PropTypes.bool,
  chevronStyle: PropTypes.oneOf(['small', 'big']),
  ratio: PropTypes.oneOf(Object.values(RATIOS)),
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.object })
  ]),
  keepHeightWhileLoading: PropTypes.bool,
  lazyLoad: PropTypes.bool,
  withBackgroundChevrons: PropTypes.bool,
  isShowStatus: PropTypes.bool,
  autoFocus: PropTypes.bool
};

const VideoCarouselWithRef = forwardRef((props, ref) => (
  <VideoCarousel {...props} forwardedRef={ref} />
));

VideoCarouselWithRef.RATIOS = RATIOS;

export default VideoCarouselWithRef;
